import { useState, useRef, useEffect } from "react";

interface LazyVideoProps {
  src: string;
  className?: string;
  style?: React.CSSProperties;
  poster?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  /** Load video immediately without waiting for viewport intersection */
  priority?: boolean;
}

/**
 * Lazy-loading video component.
 * By default the <video> element is NOT rendered until the container scrolls
 * into view (with a 300px root-margin). This avoids downloading heavy mp4
 * files that are off-screen, drastically improving load times on slow
 * connections.
 */
export const LazyVideo = ({
  src,
  className = "",
  style,
  poster,
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true,
  priority = false,
}: LazyVideoProps) => {
  const [isInView, setIsInView] = useState(priority);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (priority) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "1500px", threshold: 0 },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  return (
    <div ref={containerRef} className={className} style={style}>
      {isInView ? (
        <video
          src={src}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          preload="metadata"
          poster={poster}
          className="w-full h-full object-cover"
          style={style}
        />
      ) : (
        /* Lightweight placeholder while video hasn't entered viewport */
        <div className="w-full h-full bg-muted/30 animate-pulse" />
      )}
    </div>
  );
};
